import { DEFAULT_BOARD, SIGNS, TILE_KIND, SIGN_ELEMENT, TILE_RARITY } from './type';
import { findMatches } from './matches';
import { WORLD_IDENTITY_V2, worldIdentityBias } from './worldIdentityV2';
import { WORLD_MECHANICS_VERSION, mechanicForLevel } from './worldMechanicsV3';

// Normalize element keys to canonical mission keys used by backend/UI.
// Canonical: 'fire' | 'water' | 'air' | 'earth'
function normalizeElementKey(el) {
  const s = String(el || '').trim().toLowerCase();
  if (!s) return null;

  // Spanish -> canonical
  if (s === 'fuego') return 'fire';
  if (s === 'agua') return 'water';
  if (s === 'aire') return 'air';
  if (s === 'tierra') return 'earth';

  // Already canonical
  if (s === 'fire' || s === 'water' || s === 'air' || s === 'earth') return s;

  // Emoji / other variants (defensive)
  if (s.includes('🔥')) return 'fire';
  if (s.includes('💧')) return 'water';
  if (s.includes('🌬') || s.includes('💨')) return 'air';
  if (s.includes('🪨') || s.includes('🌍') || s.includes('⛰')) return 'earth';

  return s; // keep as-is (better than null)
}

// =====================
// World profiles (per-world board flavor)
// =====================
// Keep this lightweight: it only influences initial tile distribution.
// You can expand later with difficulty curves, obstacles, etc.
export const ZC_WORLD_PROFILES = {
  aries:   { biasElement: 'fire',  biasStrength: 0.48, elementWeights: { fire: 0.50, air: 0.30, earth: 0.12, water: 0.08 } },
  tauro:   { biasElement: 'earth', biasStrength: 0.48, elementWeights: { earth: 0.50, water: 0.30, fire: 0.12, air: 0.08 } },
  geminis: { biasElement: 'air',   biasStrength: 0.46, elementWeights: { air: 0.50, fire: 0.22, earth: 0.14, water: 0.14 } },
  cancer:  { biasElement: 'water', biasStrength: 0.48, elementWeights: { water: 0.50, earth: 0.26, air: 0.12, fire: 0.12 } },
  leo:     { biasElement: 'fire',  biasStrength: 0.50, elementWeights: { fire: 0.52, air: 0.28, earth: 0.10, water: 0.10 } },
  virgo:   { biasElement: 'earth', biasStrength: 0.46, elementWeights: { earth: 0.50, air: 0.22, water: 0.18, fire: 0.10 } },
  libra:   { biasElement: 'air',   biasStrength: 0.46, elementWeights: { air: 0.52, fire: 0.20, water: 0.16, earth: 0.12 } },
  escorpio:{ biasElement: 'water', biasStrength: 0.50, elementWeights: { water: 0.52, earth: 0.22, air: 0.14, fire: 0.12 } },
  sagitario:{biasElement:'fire',   biasStrength: 0.46, elementWeights: { fire: 0.50, air: 0.30, earth: 0.10, water: 0.10 } },
  capricornio:{biasElement:'earth',biasStrength: 0.50, elementWeights: { earth: 0.52, water: 0.22, air: 0.14, fire: 0.12 } },
  acuario: { biasElement: 'air',   biasStrength: 0.50, elementWeights: { air: 0.54, fire: 0.18, earth: 0.14, water: 0.14 } },
  piscis:  { biasElement: 'water', biasStrength: 0.50, elementWeights: { water: 0.54, air: 0.20, earth: 0.14, fire: 0.12 } },
};

export const WORLD_ELEMENT_INITIAL_WEIGHT = 0.625;
export const WORLD_ELEMENT_REFILL_WEIGHT = 0.425;

const ZC_WORLD_KEY_ALIASES = Object.freeze({
  taurus: 'tauro',
  gemini: 'geminis',
  scorpio: 'escorpio',
  sagittarius: 'sagitario',
  capricorn: 'capricornio',
  aquarius: 'acuario',
  pisces: 'piscis',
});

export function getZcWorldProfile(worldKey) {
  const k = String(worldKey || '').trim().toLowerCase();
  return ZC_WORLD_PROFILES[ZC_WORLD_KEY_ALIASES[k] || k] || null;
}

function _clamp01(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(1, x));
}

function _weightedPick(rng, items) {
  // items: [{ value, w }]
  const list = Array.isArray(items) ? items.filter((x) => x && x.w > 0) : [];
  if (!list.length) return null;

  const total = list.reduce((acc, x) => acc + x.w, 0);
  const r = typeof rng?.float === 'function' ? rng.float() : 0;
  let roll = r * total;
  for (const it of list) {
    roll -= it.w;
    if (roll <= 0) return it.value;
  }
  return list[list.length - 1].value;
}

// Select a sign from the world element group without introducing a second
// zodiac mapping. Neutral calls remain equivalent to the legacy uniform pool.
export function pickWeightedSignKey(
  rng,
  { worldElement = null, biasWeight = null, allowedSigns = null } = {}
) {
  const pool = Array.isArray(allowedSigns) && allowedSigns.length ? allowedSigns : SIGNS;
  const element = normalizeElementKey(worldElement);
  const weight = _clamp01(biasWeight);
  if (!element || weight <= 0) {
    return typeof rng?.pick === 'function' ? rng.pick(pool) : pool[0];
  }

  const preferred = pool.filter((key) => normalizeElementKey(SIGN_ELEMENT[key]) === element);
  const other = pool.filter((key) => normalizeElementKey(SIGN_ELEMENT[key]) !== element);
  if (!preferred.length || !other.length) {
    return typeof rng?.pick === 'function' ? rng.pick(pool) : pool[0];
  }

  const preferredWeight = weight / preferred.length;
  const otherWeight = (1 - weight) / other.length;
  return _weightedPick(rng, [
    ...preferred.map((value) => ({ value, w: preferredWeight })),
    ...other.map((value) => ({ value, w: otherWeight })),
  ]) || pool[0];
}

function _normalizeElementWeights(w) {
  const base = {
    fire: Number(w?.fire ?? 0),
    water: Number(w?.water ?? 0),
    air: Number(w?.air ?? 0),
    earth: Number(w?.earth ?? 0),
  };
  const sum = Object.values(base).reduce((a, x) => a + (Number.isFinite(x) ? x : 0), 0);
  if (!sum) return null;
  return {
    fire: base.fire / sum,
    water: base.water / sum,
    air: base.air / sum,
    earth: base.earth / sum,
  };
}

// =====================
// Playability (Candy-Crush-like): ensure at least 1 valid move
// =====================
// NOTE: we lazy-require swap.js to avoid circular-import issues.
function _shuffleArrayInPlace(arr, rng) {
  for (let i = arr.length - 1; i > 0; i--) {
    // Generation is deterministic; callers should provide the seeded RNG.
    const r = typeof rng?.float === 'function' ? rng.float() : 0;
    const j = Math.floor(r * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

// =====================
// Playability (Candy-Crush-like): ensure at least 1 valid move
// =====================
// IMPORTANT: do NOT import swap.js here.
// swap.js imports cloneBoard from this file; importing it back creates a
// circular dependency that can disable playability checks during startup.

function _isAdjacentLocal(a, b) {
  if (!a || !b) return false;
  const dr = Math.abs(a.r - b.r);
  const dc = Math.abs(a.c - b.c);
  return (dr + dc) === 1;
}

function _swapCreatesMatchLocal(board, a, b) {
  if (!_isAdjacentLocal(a, b)) return false;

  const t1 = board.grid?.[a.r]?.[a.c] || null;
  const t2 = board.grid?.[b.r]?.[b.c] || null;
  if (!t1 || !t2) return false;

  const swapAxis = a.r !== b.r ? 'y' : 'x';

  // Swap in place
  board.grid[a.r][a.c] = t2;
  board.grid[b.r][b.c] = t1;

  try {
    // Check both endpoints so we don't miss real moves.
    const matchesB = findMatches(board, { swapTo: b, swapAxis }) || [];
    if (matchesB.length > 0) return true;

    const matchesA = findMatches(board, { swapTo: a, swapAxis }) || [];
    return matchesA.length > 0;
  } finally {
    // Always restore original board state
    board.grid[a.r][a.c] = t1;
    board.grid[b.r][b.c] = t2;
  }
}

function _hasAnyValidMoveLocal(board) {
  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      const a = { r, c };

      // right
      if (c + 1 < board.cols) {
        const b = { r, c: c + 1 };
        if (_swapCreatesMatchLocal(board, a, b)) return true;
      }

      // down
      if (r + 1 < board.rows) {
        const b = { r: r + 1, c };
        if (_swapCreatesMatchLocal(board, a, b)) return true;
      }
    }
  }
  return false;
}

function _hasPreferredElementMoveLocal(board, elementKey) {
  const el = normalizeElementKey(elementKey);
  if (!el) return false;
  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      const a = { r, c };
      const candidates = [];
      if (c + 1 < board.cols) candidates.push({ r, c: c + 1 });
      if (r + 1 < board.rows) candidates.push({ r: r + 1, c });
      for (const b of candidates) {
        const ta = getTile(board, a.r, a.c);
        const tb = getTile(board, b.r, b.c);
        if ((ta?.element === el || tb?.element === el) && _swapCreatesMatchLocal(board, a, b)) {
          return true;
        }
      }
    }
  }
  return false;
}

// V2's opening contract is stronger than merely swapping a preferred tile:
// at least one resulting match must contain a tile of the world element.
function _hasPreferredMatchMoveLocal(board, elementKey) {
  const el = normalizeElementKey(elementKey);
  if (!el) return false;
  const hasPreferredCell = (groups) => (groups || []).some((group) =>
    (group?.cells || []).some((cell) => normalizeElementKey(getTile(board, cell.r, cell.c)?.element) === el)
  );
  for (let r = 0; r < board.rows; r += 1) {
    for (let c = 0; c < board.cols; c += 1) {
      const a = { r, c };
      const candidates = [];
      if (c + 1 < board.cols) candidates.push({ r, c: c + 1 });
      if (r + 1 < board.rows) candidates.push({ r: r + 1, c });
      for (const b of candidates) {
        const t1 = getTile(board, a.r, a.c);
        const t2 = getTile(board, b.r, b.c);
        if (!t1 || !t2) continue;
        const axis = a.r !== b.r ? 'y' : 'x';
        board.grid[a.r][a.c] = t2;
        board.grid[b.r][b.c] = t1;
        try {
          if (hasPreferredCell(findMatches(board, { swapTo: b, swapAxis: axis })) ||
              hasPreferredCell(findMatches(board, { swapTo: a, swapAxis: axis }))) return true;
        } finally {
          board.grid[a.r][a.c] = t1;
          board.grid[b.r][b.c] = t2;
        }
      }
    }
  }
  return false;
}

function _fixImmediateMatchesInPlace(board, rng) {
  // Walk top-left -> bottom-right, and if placing this tile creates an immediate match
  // (3 in a row/col), re-roll only its SIGN key a few times.
  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      const t = getTile(board, r, c);
      if (!t) continue;

      // Only SIGN tiles are part of matches today
      if (t.kind !== TILE_KIND.SIGN) continue;

      let attempts = 0;
      let key = t.key;
      while (attempts < 40 && createsImmediateMatchAnyDir(board, r, c, key)) {
        key = (typeof rng?.pick === 'function')
  ? rng.pick(SIGNS)
  : SIGNS[0];
        attempts += 1;
      }

      if (key !== t.key) {
        t.key = key;
        t.element = normalizeElementKey(SIGN_ELEMENT[key]) || null;
      }
    }
  }
}

export function shuffleBoard(board, rng, { avoidImmediateMatches = true } = {}) {
  const b = cloneBoard(board);

  // Collect tiles
  const tiles = [];
  for (let r = 0; r < b.rows; r++) {
    for (let c = 0; c < b.cols; c++) {
      const t = getTile(b, r, c);
      tiles.push(t);
    }
  }

  _shuffleArrayInPlace(tiles, rng);

  // Put back
  let k = 0;
  for (let r = 0; r < b.rows; r++) {
    for (let c = 0; c < b.cols; c++) {
      setTile(b, r, c, tiles[k++]);
    }
  }

  if (avoidImmediateMatches) {
    _fixImmediateMatchesInPlace(b, rng);
  }

  return b;
}

export function ensurePlayableBoard(board, rng, {
  maxShuffles = 25,
  avoidImmediateMatches = true,
} = {}) {
  // Fast path: already playable
  if (_hasAnyValidMoveLocal(board)) return board;

  // Shuffle until playable (Candy Crush style)
  let b = board;
  for (let i = 0; i < maxShuffles; i++) {
    b = shuffleBoard(b, rng, { avoidImmediateMatches });
    if (_hasAnyValidMoveLocal(b)) return b;
  }

  // Worst case: regenerate entirely (match-free) WITHOUT calling createInitialBoard (to avoid recursion).
  const rebuilt = _createInitialBoardRaw(rng, board.rows, board.cols, {
    worldKey: board?.meta?.worldKey || null,
    level: board?.meta?.level || 1,
    allowedSigns: board?.meta?.allowedSigns || null,
    elementWeights: board?.meta?.elementWeights || null,
    biasElement: board?.meta?.biasElement || null,
    biasStrength: board?.meta?.biasStrength ?? 0.68,
    biasWeight: board?.meta?.biasWeight ?? null,
  });

  // If still not playable (super unlikely), try a few shuffles.
  let bb = rebuilt;
  for (let j = 0; j < Math.max(8, Math.floor(maxShuffles / 2)); j++) {
    bb = shuffleBoard(bb, rng, { avoidImmediateMatches });
    if (_hasAnyValidMoveLocal(bb)) return bb;
  }

  // As a last resort, at least return a match-free board.
  if (avoidImmediateMatches) _fixImmediateMatchesInPlace(bb, rng);
  return bb;
}

// =====================
// IDs
// =====================
let _idCounter = 0;
function newId() {
  _idCounter += 1;
  return `${Date.now().toString(36)}_${_idCounter.toString(36)}`;
}

// =====================
// Board helpers
// =====================
export function createEmptyBoard(rows = DEFAULT_BOARD.rows, cols = DEFAULT_BOARD.cols) {
  return {
    rows,
    cols,
    grid: Array.from({ length: rows }, () => Array.from({ length: cols }, () => null)),
    meta: { hole: null },
  };
}

export function cloneBoard(board) {
  return {
    rows: board.rows,
    cols: board.cols,
    grid: board.grid.map((row) => row.map((t) => (t ? { ...t } : null))),
    meta: board.meta ? { ...board.meta } : { hole: null },
  };
}

export function inBounds(board, r, c) {
  return r >= 0 && c >= 0 && r < board.rows && c < board.cols;
}

export function getTile(board, r, c) {
  if (!inBounds(board, r, c)) return null;
  return board.grid[r][c];
}

export function setTile(board, r, c, tile) {
  if (!inBounds(board, r, c)) return;
  board.grid[r][c] = tile;
}

// =====================
// Tile factories
// =====================
export function makeSignTile(key) {
  return {
    id: newId(),
    kind: TILE_KIND.SIGN,
    key,
    element: normalizeElementKey(SIGN_ELEMENT[key]) || null,
    rarity: TILE_RARITY.NORMAL,
  };
}

// (Reservado para futuro)
export function makeElementTile(key) {
  return {
    id: newId(),
    kind: TILE_KIND.ELEMENT,
    key,
    element: key || null,
    rarity: TILE_RARITY.RARE,
  };
}

export function makePlanetTile(key) {
  return {
    id: newId(),
    kind: TILE_KIND.PLANET,
    key,
    element: null,
    rarity: TILE_RARITY.EPIC,
  };
}

// =====================
// Initial board (sin matches inmediatos)
// =====================
function createsImmediateMatch(board, r, c, key) {
  // izquierda-izquierda
  const a = getTile(board, r, c - 1);
  const b = getTile(board, r, c - 2);
  if (
    a &&
    b &&
    a.kind === TILE_KIND.SIGN &&
    b.kind === TILE_KIND.SIGN &&
    a.key === key &&
    b.key === key
  ) {
    return true;
  }

  // arriba-arriba
  const u1 = getTile(board, r - 1, c);
  const u2 = getTile(board, r - 2, c);
  if (
    u1 &&
    u2 &&
    u1.kind === TILE_KIND.SIGN &&
    u2.kind === TILE_KIND.SIGN &&
    u1.key === key &&
    u2.key === key
  ) {
    return true;
  }

  return false;
}

// Full-direction immediate-match check.
// Use this ONLY when the board is already fully populated (e.g., after shuffles),
// because a change at [r,c] can create a match to the right/down as well.
function createsImmediateMatchAnyDir(board, r, c, key) {
  // Left-left
  const l1 = getTile(board, r, c - 1);
  const l2 = getTile(board, r, c - 2);
  if (
    l1 &&
    l2 &&
    l1.kind === TILE_KIND.SIGN &&
    l2.kind === TILE_KIND.SIGN &&
    l1.key === key &&
    l2.key === key
  ) return true;

  // Right-right
  const r1 = getTile(board, r, c + 1);
  const r2 = getTile(board, r, c + 2);
  if (
    r1 &&
    r2 &&
    r1.kind === TILE_KIND.SIGN &&
    r2.kind === TILE_KIND.SIGN &&
    r1.key === key &&
    r2.key === key
  ) return true;

  // Up-up
  const u1 = getTile(board, r - 1, c);
  const u2 = getTile(board, r - 2, c);
  if (
    u1 &&
    u2 &&
    u1.kind === TILE_KIND.SIGN &&
    u2.kind === TILE_KIND.SIGN &&
    u1.key === key &&
    u2.key === key
  ) return true;

  // Down-down
  const d1 = getTile(board, r + 1, c);
  const d2 = getTile(board, r + 2, c);
  if (
    d1 &&
    d2 &&
    d1.kind === TILE_KIND.SIGN &&
    d2.kind === TILE_KIND.SIGN &&
    d1.key === key &&
    d2.key === key
  ) return true;

  return false;
}

// =====================
// World/element bias helpers (v2: worldKey + allowedSigns + elementWeights)
// =====================
function _signsForElement(elementKey, pool = null) {
  const el = normalizeElementKey(elementKey);
  const basePool = Array.isArray(pool) && pool.length ? pool : SIGNS;
  if (!el) return basePool;
  const filtered = basePool.filter((k) => normalizeElementKey(SIGN_ELEMENT[k]) === el);
  return filtered.length ? filtered : basePool;
}

function _pickSignKeyWorld(
  rng,
  {
    // world flavor
    worldKey = null,
    allowedSigns = null,
    elementWeights = null,

    // legacy bias (kept for backwards compatibility)
    biasElement = null,
    biasStrength = 0.68,
    biasWeight = null,
  } = {}
) {
  const pool = Array.isArray(allowedSigns) && allowedSigns.length ? allowedSigns : SIGNS;

  // If a worldKey exists and elementWeights not explicitly provided, load defaults.
  const prof = worldKey ? getZcWorldProfile(worldKey) : null;
  const profWeights = _normalizeElementWeights(prof?.elementWeights);
  const wNorm = _normalizeElementWeights(elementWeights) || profWeights;

  // Resolve bias from explicit args OR profile.
  const biasEl = normalizeElementKey(biasElement || prof?.biasElement || null);
  const biasP = _clamp01(biasStrength ?? prof?.biasStrength ?? 0.68);

  if (biasEl && biasWeight != null) {
    return pickWeightedSignKey(rng, {
      worldElement: biasEl,
      biasWeight,
      allowedSigns: pool,
    });
  }

  // --- Weighted mode (preferred): choose sign weights derived from element weights (+ optional bias)
  if (wNorm) {
    const signItems = pool.map((k) => {
      const el = normalizeElementKey(SIGN_ELEMENT[k]);
      const baseW = Number(wNorm?.[el] ?? 0.0001) || 0.0001;
      // Apply a gentle bias multiplier (keeps variety; avoids "all fire")
      let w = baseW;
      if (biasEl && el === biasEl) w = baseW * (1 + biasP * 1.35);
      else if (biasEl && el && el !== biasEl) w = baseW * (1 - biasP * 0.35);
      // Defensive clamp
      w = Math.max(0.0001, w);
      return { value: k, w };
    });

    const picked = _weightedPick(rng, signItems);
    return picked || (typeof rng?.pick === 'function' ? rng.pick(pool) : pool[0]);
  }

  // --- Legacy bias mode (fallback): biasElement with probability biasStrength
  const p = _clamp01(biasP);
  const useBias = biasEl && p > 0;
  if (useBias) {
    const r = typeof rng?.float === 'function' ? rng.float() : 0;
    if (r < p) {
      const biasedPool = _signsForElement(biasEl, pool);
      return typeof rng?.pick === 'function' ? rng.pick(biasedPool) : biasedPool[0];
    }
  }

  return typeof rng?.pick === 'function' ? rng.pick(pool) : pool[0];
}

function _createInitialBoardRaw(
  rng,
  rows = DEFAULT_BOARD.rows,
  cols = DEFAULT_BOARD.cols,
  {
    worldKey = null,
    level = 1,
    allowedSigns = null,
    elementWeights = null,
    biasElement = null,
    biasStrength = 0.68,
    biasWeight = null,
  } = {}
) {
  const b = createEmptyBoard(rows, cols);

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      // Elegimos un signo que no genere match inmediato.
      let attempts = 0;
      let key = _pickSignKeyWorld(rng, { worldKey, level, allowedSigns, elementWeights, biasElement, biasStrength, biasWeight });
      while (attempts < 40 && createsImmediateMatch(b, r, c, key)) {
        key = _pickSignKeyWorld(rng, { worldKey, level, allowedSigns, elementWeights, biasElement, biasStrength, biasWeight });
        attempts += 1;
      }
      setTile(b, r, c, makeSignTile(key));
    }
  }

  // Safety: ensure element exists on all tiles (future-proof / legacy-proof)
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const t = getTile(b, r, c);
      if (t && typeof t.element === 'undefined') {
        // SIGN tiles should always map to an element
        if (t.kind === TILE_KIND.SIGN) t.element = normalizeElementKey(SIGN_ELEMENT[t.key]) || null;
        else if (t.kind === TILE_KIND.ELEMENT) t.element = normalizeElementKey(t.key) || null;
        else t.element = null;
      }
      if (t && typeof t.element !== 'undefined') {
        t.element = normalizeElementKey(t.element) || null;
      }
      if (t && typeof t.rarity === 'undefined') {
        t.rarity = t.kind === TILE_KIND.PLANET
          ? TILE_RARITY.EPIC
          : (t.kind === TILE_KIND.ELEMENT ? TILE_RARITY.RARE : TILE_RARITY.NORMAL);
      }
    }
  }

  return b;
}

export function createInitialBoard(
  rng,
  rows = DEFAULT_BOARD.rows,
  cols = DEFAULT_BOARD.cols,
  {
    worldKey = null,
    level = 1,
    allowedSigns = null,
    elementWeights = null,
    biasElement = null,
    biasStrength = null,
    biasWeight = null,
    worldIdentityVersion = 1,
  } = {}
) {
  const prof = worldKey ? getZcWorldProfile(worldKey) : null;
  const resolvedBiasElement = normalizeElementKey(biasElement || prof?.biasElement || null);
  const resolvedBiasStrength = Number(biasStrength ?? prof?.biasStrength ?? 0.68);
  const resolvedBiasWeight = biasWeight == null && resolvedBiasElement && biasStrength == null
    ? WORLD_ELEMENT_INITIAL_WEIGHT
    : biasWeight;
  const v2BiasWeight = Number(worldIdentityVersion) === WORLD_IDENTITY_V2.version && resolvedBiasElement
    ? (biasWeight == null ? worldIdentityBias(((Number(level || 1) - 1) % 20) + 1) : biasWeight)
    : resolvedBiasWeight;
  const b = _createInitialBoardRaw(rng, rows, cols, {
    worldKey,
    level,
    allowedSigns,
    elementWeights,
    biasElement,
    biasStrength: resolvedBiasStrength,
    biasWeight: v2BiasWeight,
  });

  // Persist world flavor in board meta so reshuffles/regens keep the same flavor
  const resolvedWeights = _normalizeElementWeights(elementWeights) || _normalizeElementWeights(prof?.elementWeights) || null;

  b.meta = {
    ...(b.meta || {}),
    worldKey: worldKey ? String(worldKey).toLowerCase() : null,
    level: Number(level || 1) || 1,
    biasElement: resolvedBiasElement,
    biasStrength: resolvedBiasStrength,
    biasWeight: v2BiasWeight,
    elementWeights: resolvedWeights,
    allowedSigns: Array.isArray(allowedSigns) && allowedSigns.length ? allowedSigns.slice() : null,
  };

  // Ensure at least one valid move exists (no dead boards). For world-aware
  // starts, make a bounded attempt to prefer an opening move involving the
  // world element without changing swap semantics or creating fake moves.
  const makePlayable = (candidate) => ensurePlayableBoard(candidate, rng, {
    maxShuffles: 40,
    avoidImmediateMatches: true,
  });
  let playable = makePlayable(b);
  if (Number(worldIdentityVersion) === WORLD_IDENTITY_V2.version && resolvedBiasElement) {
    // V2 uses the shared level curve and a bounded, deterministic opening
    // search. V1 follows the legacy path below unchanged.
    const v2Weight = biasWeight == null ? worldIdentityBias(((Number(level || 1) - 1) % 20) + 1) : biasWeight;
    playable.meta = { ...playable.meta, worldIdentityVersion: WORLD_IDENTITY_V2.version, biasWeight: v2Weight };
    if (_hasPreferredMatchMoveLocal(playable, resolvedBiasElement)) return playable;
    for (let retry = 0; retry < 32; retry += 1) {
      const candidate = _createInitialBoardRaw(rng, rows, cols, {
        worldKey, level, allowedSigns, elementWeights,
        biasElement: resolvedBiasElement,
        biasStrength: resolvedBiasStrength,
        biasWeight: v2Weight,
      });
      candidate.meta = { ...playable.meta, worldIdentityVersion: WORLD_IDENTITY_V2.version, biasWeight: v2Weight };
      playable = makePlayable(candidate);
      if (_hasPreferredMatchMoveLocal(playable, resolvedBiasElement)) return playable;
    }
    // Fail closed: V2 must never silently produce a board that violates its
    // opening contract.
    throw new Error('world_identity_v2_opening_move_unavailable');
  }
  if (resolvedBiasWeight == null || !resolvedBiasElement || _hasPreferredElementMoveLocal(playable, resolvedBiasElement)) {
    return playable;
  }
  for (let retry = 0; retry < 3; retry += 1) {
    const candidate = _createInitialBoardRaw(rng, rows, cols, {
      worldKey,
      level,
      allowedSigns,
      elementWeights,
      biasElement,
      biasStrength: resolvedBiasStrength,
      biasWeight: resolvedBiasWeight,
    });
    candidate.meta = { ...playable.meta };
    playable = makePlayable(candidate);
    if (_hasPreferredElementMoveLocal(playable, resolvedBiasElement)) return playable;
  }
  return playable;
}

export function createInitialBoardV2(rng, rows = DEFAULT_BOARD.rows, cols = DEFAULT_BOARD.cols, options = {}) {
  return createInitialBoard(rng, rows, cols, {
    ...options,
    worldIdentityVersion: WORLD_IDENTITY_V2.version,
  });
}

export function applyWorldMechanicsV3(board, rng, { worldElement, indexInWorld = 1, worldIndex = 0 } = {}) {
  const rule = mechanicForLevel(worldElement, indexInWorld, worldIndex);
  const state = Array.from({ length: board.rows }, () => Array(board.cols).fill(null));
  const type = rule.family === 'volcanic' ? 'volcanic' : rule.family === 'stone' ? 'stone' : rule.family === 'water_layer' ? 'water_layer' : null;
  if (!type) return { ...board, meta: { ...(board.meta || {}), worldMechanicsVersion: WORLD_MECHANICS_VERSION, mechanicState: state } };
  let placed = 0;
  while (placed < rule.density && placed < 4) {
    const r = rng.int(0, board.rows - 1);
    const c = rng.int(0, board.cols - 1);
    if (state[r][c]) continue;
    state[r][c] = { type, hp: type === 'stone' ? Math.min(4, 1 + Math.floor((indexInWorld - 1) / 7)) : 1 };
    placed += 1;
  }
  return { ...board, meta: { ...(board.meta || {}), worldMechanicsVersion: WORLD_MECHANICS_VERSION, mechanicState: state } };
}

// Re-check playability after any refill/settle. Intended to be called by the resolver.
export function ensurePlayableAfterResolve(board, rng) {
  // 🔒 CRITICAL GUARD:
  // If there are empty cells, the resolver (gravity/refill) must handle them.
  // Board-level playability checks must NOT run while holes exist,
  // otherwise 3-matches get auto-filled and you lose the empty space.
  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      if (board.grid[r][c] == null) {
        return board;
      }
    }
  }

  // Only ensure playability when the board is fully settled
  return ensurePlayableBoard(board, rng, { maxShuffles: 40, avoidImmediateMatches: true });
}
